import { gql, useMutation } from '@apollo/client';
import Button from '@orn/shared-web/components/Button/Button';
import Modal from '@orn/shared-web/components/Modal';
import logger from '@orn/shared-web/lib/logger';

import type {
  DeleteFinancialAccountMutation,
  DeleteFinancialAccountMutationVariables,
} from './__generated__/DeleteFinancialAccountModal.gql';

const DELETE_FINANCIAL_ACCOUNT = gql`
  mutation DeleteFinancialAccount($input: DeleteFinancialAccountInput!) {
    deleteFinancialAccount(input: $input)
  }
`;

interface IAccount {
  id: string;
  name: string;
}

interface IBaseDeleteFinancialAccountModalProps {
  account: IAccount;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onConfirm: () => void;
  isDeleting?: boolean;
  /** Error from a failed delete attempt; undefined hides the message. */
  error?: string;
}

export const BaseDeleteFinancialAccountModal = ({
  account,
  isOpen,
  onOpenChange,
  onConfirm,
  isDeleting = false,
  error,
}: IBaseDeleteFinancialAccountModalProps) => {
  const handleClose = () => onOpenChange(false);

  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange} size="sm">
      <Modal.Header onClose={handleClose}>Hide {account.name}?</Modal.Header>
      <Modal.Body className="gap-5 px-6 pb-6">
        <div className="text-secondary text-sm">This hides the account and all of its transactions from Mantle.</div>
        {error && <span className="text-error-primary w-full text-sm">{error}</span>}
        <div className="flex w-full justify-end gap-3">
          <Button color="secondary" size="md" onClick={handleClose} isDisabled={isDeleting}>
            Cancel
          </Button>
          <Button color="primary-destructive" size="md" onClick={onConfirm} isLoading={isDeleting} showTextWhileLoading>
            {isDeleting ? 'Hiding' : 'Hide account'}
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

interface IDeleteFinancialAccountModalProps {
  account: IAccount;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  /** Called after the delete succeeds — the caller owns any navigation away from the deleted account. */
  onDeleted?: () => void;
}

/**
 * Confirmation modal for hiding a connected financial account. Owns the
 * `deleteFinancialAccount` mutation; cache updates are centralized in
 * ApolloProvider. Renders account-specific copy so the user can verify they
 * are hiding the right account.
 */
const DeleteFinancialAccountModal = ({
  account,
  isOpen,
  onOpenChange,
  onDeleted,
}: IDeleteFinancialAccountModalProps) => {
  const [deleteFinancialAccount, { loading, error }] = useMutation<
    DeleteFinancialAccountMutation,
    DeleteFinancialAccountMutationVariables
  >(DELETE_FINANCIAL_ACCOUNT);

  const handleConfirm = async () => {
    try {
      await deleteFinancialAccount({ variables: { input: { id: account.id } } });
      onOpenChange(false);
      onDeleted?.();
    } catch (deleteError) {
      logger.error('Failed to delete financial account:', deleteError);
    }
  };

  return (
    <BaseDeleteFinancialAccountModal
      account={account}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      onConfirm={handleConfirm}
      isDeleting={loading}
      error={error ? 'Something went wrong hiding this account. Please try again.' : undefined}
    />
  );
};

export default DeleteFinancialAccountModal;
